import { call, put, race, select, setContext, spawn, take, takeEvery } from 'redux-saga/effects';

import dynaForms, { dtoBuilder } from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { actions as authAction } from '@elab/core/store/auth';
import { actions as userActions, selectors as userSelectors } from '@elab/core/store/user';
import { actions as globalActions } from '@elab/core/store/global';
import {
    actions as verifyPhoneFormActions,
    selectors as verifyPhoneFormSelectors,
    types as verifyPhoneFormTypes,
} from '@elab/components/connected/forms/PhoneVerificationForm/redux';
import isEmpty from '@elab/core/utils/isEmpty';
import { phoneNumberChangeFormSuccessMessage } from '@elab/core/utils/constants';

import api from './api';

import { actions, selectors, types } from '.';

export function* doSubmit() {
    const data = yield select(selectors.getData);
    const shouldVerifyPhone = !isEmpty(yield select(verifyPhoneFormSelectors.getData));
    const checkoutFlow = yield select(selectors.isCheckoutFlow);
    if (shouldVerifyPhone) {
        if (checkoutFlow) {
            yield call(verifyPhone, data, selectors);
        } else {
            yield spawn(verifyPhone, data, selectors);
        }
    } else {
        yield doPhoneUpdate();
        yield put(actions.toggleSuccessMsg());
    }
}

export function* customDtoBuilder(...args) {
    let dto = yield call(dtoBuilder, ...args);
    dto = yield call(userDtoCombinator, dto);
    return dto;
}

export function* userDtoCombinator(dto) {
    let userDto = yield select(userSelectors.getProfile);
    if (userDto.anonymous) {
        userDto = yield select(selectors.getUserProfile);
    }
    userDto.defaultAddress.mobileNumber = dto.defaultAddress.mobileNumber;
    return { ...userDto };
}

export function* verifyPhone() {
    yield put(verifyPhoneFormActions.startVerification('__extension'));

    const { canContinue } = yield race({
        canContinue: take(verifyPhoneFormTypes.COMPLETE_VERIFICATION),
        cancel: take(verifyPhoneFormTypes.EDIT_PHONE),
        halt: take(types.EDIT_CHECKOUT_FLOW),
    });
    if (!canContinue) {
        return;
    }
    yield call(doPhoneUpdate);
}

export function* doPhoneUpdate() {
    const isPopup = yield select(selectors.getUseAsPopup);
    let userDto = yield select(userSelectors.getProfile);
    if (userDto.anonymous) {
        userDto = yield select(selectors.getUserProfile);
    }
    const sessionField = yield select(verifyPhoneFormSelectors.getSessionId);
    if (sessionField) {
        userDto.defaultAddress.phoneVerificationSessionId = sessionField;
    }
    yield call(api.confirmCrm, userDto);
    if (!isPopup) {
        yield put(actions.resetFormData());
        yield put(globalActions.addMessage(phoneNumberChangeFormSuccessMessage));
        yield put(userActions.loadProfile());
    }
    yield put(actions.phoneNumberUpdated());
    yield put(actions.useAsPopup(false));
}

export function* closeAndLogOut() {
    const checkoutFlow = yield select(selectors.isCheckoutFlow);
    if (checkoutFlow) {
        yield put(actions.interruptLogin());
    } else {
        yield put(authAction.doLogout('/'));
    }
}

export default storeName => formId => {
    const reduxArtifacts = { storeName, actions, selectors, types };
    return [
        setContext({ dialogName: 'update-phone-number-form-dialog' }),
        ...dynaForms.createSagaFor(
            reduxArtifacts,
            dynaFormsApi.getFormMetadata(formId),
            doSubmit,
            null,
            true,
            customDtoBuilder,
        ),
        takeEvery(types.CLOSE_AND_LOGOUT, closeAndLogOut),
    ];
};
